import { BCMSDate, DisabledPrompt, Link, useRoute, useParams, useModel } from "@bcm-dev/bcms-vite";
import React, { useState } from "react";

import MainContentContainer, { MainContentHeader } from "components/shared/containers/MainContentContainer";
import StatementView from "components/shared/views/BaseRowView/components/StatementView";

import "./styles.css";

/**
 * @function StatementsPage
 * @return {React.Component}
 * @exported true
 * @description The main component for StatementsPage
 */

export default function StatementsPage(props) {
  const cardID = useParams().cardID ?? "0";
  const navigate = useRoute();
  // Create a new array with a size of 6, then map over the array and create a new BCMSDate offsetting the month by the index
  const months = Array.apply(null, Array(6)).map((item, index) => BCMSDate.init().setToFirst().offset([ 0, -index, 0 ]), []);
  // Get all the year values from the months, remove any duplicates
  const years = [ ...new Set( months.reduce((result, item) => [ ...result, item.yearInt ], []) ) ];
  // Given the year, return all the months that are in the same year
  const getMonthsByYear = year => months.filter(item => item.yearInt === year, []);
  // Create a state to keep track of the selected year
  const [ state, setState ] = useState(years[0]);
  const update = value => setState(value);

  const tabItems = years.sort().map(year => <YearTabItem key={ `Year_${ year }` } year={ year } activeYear={ state } onClick={ update } />, []);
  const statements = years.map(year => <StatementYearContent key={ `Statement_Year_${ year }` } year={ year } activeYear={ state } months={ getMonthsByYear(year) } />, []);

  return (
      <MainContentContainer className="statements-page" header={
        <MainContentHeader text="Statements" onClick={ evt => navigate(`/home/recenttransactions/${ cardID }`) } />
      }>
        <p>You are currently receiving Email and Postmail statements. View and update your delivery options <DisabledPrompt><Link>here</Link></DisabledPrompt>.</p>
        <div className="years-tabs-container flex-container flex-horizontal align-center-container">
          { tabItems }
        </div>

        { statements }
      </MainContentContainer>
  );
}

/**
 * @function YearTabItem
 * @param {BCMSDate} year - The year that should be shown
 * @param {STRING} activeYear - The current year that is selected
 * @param {FUNCTION} onClick - Function to update the year that is selected
 * @returns {React.Component}
 * @exported true
 * @description Component to create a clickable button to control which year is shown
 */
export function YearTabItem(props) {
  const year = props.year ?? BCMSDate.init().yearInt;
  const isActive = year === props.activeYear;
  const className = isActive ? "active-year-tab" : "";
  // If the year isn't the current year OR the current month is June or past June then this button should have
  // the data point
  const shouldHavePoint = year !== BCMSDate.init().yearInt || BCMSDate.init().monthInt >= 6;
  const point = shouldHavePoint ? { "data-point" : "Statements_Previous_Year" } : {};
  
  const onClick = evt => props.onClick instanceof Function
    ? props.onClick(year)
    : () => undefined;

  return (
    <div className={ `year-tab-item text-center clickable ${ className }` } onClick={ onClick }>
      <span className="year-tab-text" { ...point }>{year}</span>
    </div>
  )
}

/**
 * @function StatementYearContent
 * @param {BCMSDate} year - The year the content should be shown for
 * @param {ARRAY} months - The months of statements that should be shown
 * @param {STRING} activeYear - The year that is selected in state
 * @returns {React.Component}
 * @exported true
 * @description Component to create the statements for a year and show/hide based on the selected year
 */
export function StatementYearContent(props) {
  const year = props.year ?? BCMSDate.init().yearInt;
  const months = props.months ?? [];
  const isActive = year === props.activeYear;

  const { branding } = useModel();
  const openImage = evt => window.open(branding.colors.statement_chw23, "Image", "width=800,height=600,resizable=1");

  const statements = months.map(month => {
    const isCurrent = month.isEqual(BCMSDate.init().setToFirst());
    const item = (
      <StatementView
        key={ `Statement_${ month.monthInt }` }
        date={ month.format("M d, Y") }
        onClick={ isCurrent ? openImage : evt => undefined }
        dataPoint={ isCurrent ? "Statements_Statement_0" : "" }
        />
    );

    return !isCurrent ? <DisabledPrompt key={ `Disabled_${ month.monthInt }` }>{ item }</DisabledPrompt> : item;
  }, []);

  return isActive && <div className="statement-year-content">{ statements }</div>;
}